import type { Attribut } from '@/models/interfaces/Attribut'
import { MartinStyleService } from '@/services/map/MartinStyleService'
import { booleanIntersects, booleanPointInPolygon, point } from '@turf/turf'
import type { Geometry, MultiPolygon, Point, Polygon } from 'geojson'
import type {
  FilterSpecification,
  LayerSpecification,
  Map as MaplibreMap,
  SourceSpecification,
  VectorSourceSpecification,
} from 'maplibre-gl'

const visibilityCache = new Map<string, boolean>()
const pendingChecks = new Map<string, Promise<boolean>>()

type CommuneGeometry = Polygon | MultiPolygon

interface CommuneIndicatorFilterOptions {
  map: MaplibreMap | null | undefined
  communeGeometry: CommuneGeometry | null | undefined
  communeKey: string
}

type QueryableVectorLayer = LayerSpecification & {
  source: string
  'source-layer': string
  filter?: FilterSpecification
}

interface QueryStyleLayer {
  layer: QueryableVectorLayer
  source: VectorSourceSpecification
}

const DEBUG_INDICATOR_COMMUNE_FILTER =
  import.meta.env.DEV && String(import.meta.env.VITE_DEBUG_INDICATOR_COMMUNE_FILTER ?? '') === '1'

function getCacheKey(communeKey: string, indicatorId: number): string {
  return `${communeKey.trim().toLowerCase()}::${indicatorId}`
}

function isVectorSource(source: SourceSpecification | undefined): source is VectorSourceSpecification {
  return !!source && source.type === 'vector'
}

function resolveQueryStyleLayers(style: {
  layers?: LayerSpecification[]
  sources?: Record<string, SourceSpecification>
} | null): QueryStyleLayer[] {
  if (!style?.layers?.length || !style.sources) return []

  const result: QueryStyleLayer[] = []
  const seen = new Set<string>()

  for (const layer of style.layers) {
    const sourceId = 'source' in layer ? layer.source : null
    const sourceLayer = 'source-layer' in layer ? layer['source-layer'] : null
    if (typeof sourceId !== 'string' || typeof sourceLayer !== 'string') continue
    const source = style.sources[sourceId]
    if (!isVectorSource(source)) continue

    const dedupeKey = `${sourceId}::${sourceLayer}::${JSON.stringify('filter' in layer ? layer.filter : null)}`
    if (seen.has(dedupeKey)) continue
    seen.add(dedupeKey)

    result.push({
      layer: layer as QueryableVectorLayer,
      source,
    })
  }

  return result
}

function buildInvisibleLayer(
  sourceId: string,
  sourceLayer: string,
  baseLayer: QueryableVectorLayer,
  layerId: string,
): LayerSpecification {
  const common = {
    id: layerId,
    source: sourceId,
    'source-layer': sourceLayer,
    filter: baseLayer.filter,
    minzoom: baseLayer.minzoom,
    maxzoom: baseLayer.maxzoom,
  }

  switch (baseLayer.type) {
    case 'line':
      return {
        ...common,
        type: 'line',
        paint: { 'line-opacity': 0 },
      }
    case 'circle':
      return {
        ...common,
        type: 'circle',
        paint: { 'circle-opacity': 0, 'circle-stroke-opacity': 0 },
      }
    case 'symbol':
      return {
        ...common,
        type: 'symbol',
        layout: { visibility: 'visible' },
        paint: { 'icon-opacity': 0, 'text-opacity': 0 },
      }
    default:
      return {
        ...common,
        type: 'fill',
        paint: { 'fill-opacity': 0 },
      }
  }
}

async function waitForSourceLoad(map: MaplibreMap, sourceId: string): Promise<void> {
  if (map.isSourceLoaded(sourceId)) return

  await new Promise<void>((resolve) => {
    const timeout = window.setTimeout(() => {
      map.off('sourcedata', onSourceData)
      resolve()
    }, 1200)

    const onSourceData = (event: { sourceId?: string; isSourceLoaded?: boolean }) => {
      if (event.sourceId !== sourceId) return
      if (!event.isSourceLoaded && !map.isSourceLoaded(sourceId)) return
      window.clearTimeout(timeout)
      map.off('sourcedata', onSourceData)
      resolve()
    }

    map.on('sourcedata', onSourceData)
  })
}

function geometryIntersectsCommune(geometry: Geometry, communeGeometry: CommuneGeometry): boolean {
  const communeFeature = { type: 'Feature' as const, properties: {}, geometry: communeGeometry }

  if (geometry.type === 'Point') {
    return booleanPointInPolygon(point((geometry as Point).coordinates), communeFeature)
  }

  if (geometry.type === 'MultiPoint') {
    return geometry.coordinates.some((coordinates) => booleanPointInPolygon(point(coordinates), communeFeature))
  }

  return booleanIntersects(
    { type: 'Feature' as const, properties: {}, geometry },
    communeFeature,
  )
}

async function checkIndicatorVisibilityOnCommune(
  indicatorId: number,
  options: CommuneIndicatorFilterOptions,
): Promise<boolean> {
  const { map, communeGeometry } = options
  if (!map || !communeGeometry) return true

  const style = await MartinStyleService.loadStyleFromBackend(indicatorId)
  const queryLayers = resolveQueryStyleLayers(style as {
    layers?: LayerSpecification[]
    sources?: Record<string, SourceSpecification>
  } | null)

  if (!queryLayers.length) {
    return false
  }

  for (let index = 0; index < queryLayers.length; index += 1) {
    const queryLayer = queryLayers[index]
    const tempSourceId = `indicator-commune-check-source-${indicatorId}-${index}`
    const tempLayerId = `indicator-commune-check-layer-${indicatorId}-${index}`
    let addedSource = false
    let addedLayer = false

    try {
      if (!map.getSource(tempSourceId)) {
        map.addSource(tempSourceId, queryLayer.source)
        addedSource = true
      }

      if (!map.getLayer(tempLayerId)) {
        map.addLayer(
          buildInvisibleLayer(tempSourceId, queryLayer.layer['source-layer'], queryLayer.layer, tempLayerId),
        )
        addedLayer = true
      }

      await waitForSourceLoad(map, tempSourceId)

      const sourceFeatures = map.querySourceFeatures(tempSourceId, {
        sourceLayer: queryLayer.layer['source-layer'],
        filter: queryLayer.layer.filter,
      })

      const hasIntersection = sourceFeatures.some((feature) => {
        if (!feature.geometry) return false
        return geometryIntersectsCommune(feature.geometry as Geometry, communeGeometry)
      })

      if (DEBUG_INDICATOR_COMMUNE_FILTER) {
        console.info('[IndicatorCommuneVisibility] layer check', {
          indicatorId,
          communeKey: options.communeKey,
          layerIndex: index,
          sourceId: queryLayer.layer.source,
          sourceLayer: queryLayer.layer['source-layer'],
          filter: queryLayer.layer.filter ?? null,
          featureCount: sourceFeatures.length,
          hasIntersection,
        })
      }

      if (hasIntersection) {
        return true
      }
    } catch {
      if (DEBUG_INDICATOR_COMMUNE_FILTER) {
        console.warn('[IndicatorCommuneVisibility] layer check failed', {
          indicatorId,
          communeKey: options.communeKey,
          layerIndex: index,
        })
      }
      // Continue on next layer; strict mode avoids permissive fallback.
    } finally {
      if (addedLayer && map.getLayer(tempLayerId)) {
        map.removeLayer(tempLayerId)
      }
      if (addedSource && map.getSource(tempSourceId)) {
        map.removeSource(tempSourceId)
      }
    }
  }

  if (DEBUG_INDICATOR_COMMUNE_FILTER) {
    console.info('[IndicatorCommuneVisibility] no intersecting geometry found', {
      indicatorId,
      communeKey: options.communeKey,
      testedLayers: queryLayers.length,
    })
  }

  return false
}

export async function filterIndicatorsForSelectedCommune(
  indicators: Attribut[],
  options: CommuneIndicatorFilterOptions,
): Promise<Attribut[]> {
  const communeKey = options.communeKey.trim()
  if (!communeKey || !options.map || !options.communeGeometry) {
    return indicators
  }

  const checks = indicators.map(async (indicator) => {
    const cacheKey = getCacheKey(communeKey, indicator.atgId)
    const cached = visibilityCache.get(cacheKey)
    if (typeof cached === 'boolean') {
      return { indicator, visible: cached }
    }

    const pending = pendingChecks.get(cacheKey)
    if (pending) {
      const visible = await pending
      return { indicator, visible }
    }

    const request = checkIndicatorVisibilityOnCommune(indicator.atgId, options)
    pendingChecks.set(cacheKey, request)
    const visible = await request
    pendingChecks.delete(cacheKey)
    visibilityCache.set(cacheKey, visible)
    return { indicator, visible }
  })

  const results = await Promise.all(checks)
  return results.filter((entry) => entry.visible).map((entry) => entry.indicator)
}

export function clearIndicatorCommuneVisibilityCache(): void {
  visibilityCache.clear()
  pendingChecks.clear()
}
